import * as Sqrl from 'squirrelly';
import {getSizedImageUrl} from '@shopify/theme-images';

import * as a11y from '../vendor/theme-scripts/theme-a11y';
import FetchError from '../util/fetch-error';
import {fetchProduct} from '../util/fetch-product';
import {formatPrices} from '../util/format-prices';
import debounce from '../util/debounce';

const selectors = {
  append: '[data-predictive-search-append]',
  input: 'data-predictive-search-input',
  productTemplate: '[product-grid-item-template]',
  productWrapper: '[data-product-wrap]',
  productWrapperOuter: '[data-product-wrap-outer]',
  titleTemplate: '[data-predictive-search-title-template]',
  titleWrapper: '[data-search-title-wrap]',
  dirtyClass: 'dirty',
  loadingClass: 'is-loading',
  searchPopdown: 'search-popdown',
};

class SearchPredictive {
  constructor(input) {
    this.input = input;
    this.key = this.input.getAttribute(selectors.input);
    const appendSelector = `[id='${this.key}']`;
    this.append = document.querySelector(appendSelector);
    this.productTemplate = document.querySelector(selectors.productTemplate).innerHTML;
    this.titleTemplate = document.querySelector(selectors.titleTemplate).innerHTML;
    this.titleWrapper = document.querySelector(selectors.titleWrapper);
    this.productWrapper = this.append.querySelector(selectors.productWrapper);
    this.productWrapperOuter = this.append.querySelector(selectors.productWrapperOuter);
    this.popdown = document.getElementById(selectors.searchPopdown);
    this.result = null;
    this.accessibility = a11y;
    this.initSearch();
  }

  initSearch() {
    this.input.addEventListener(
      'input',
      debounce(
        function (event) {
          const val = event.target.value;
          if (val && val.length > 1) {
            this.productWrapperOuter.classList.add(selectors.loadingClass);
            this.render(val);
          } else {
            this.reset();
            this.append.classList.remove(selectors.dirtyClass);
          }
        }.bind(this),
        300
      )
    );
    this.input.addEventListener('clear', this.reset.bind(this));
  }

  render(terms) {
    fetch(`/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`)
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.result = response.resources.results;
        return this.fetchProducts(response.resources.results.products);
      })
      .then((response) => {
        this.injectTitle(terms);

        setTimeout(() => {
          this.reset(false);
          this.productWrapperOuter.classList.remove(selectors.loadingClass);
          this.injectProduct(response);
          this.append.classList.add(selectors.dirtyClass);

          this.accessibility.trapFocus(this.popdown);
          this.input.focus();
        }, 1000);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  reset(clearTerms = true) {
    this.productWrapper.innerHTML = '';
    this.append.classList.remove(selectors.dirtyClass);
    this.input.val = '';
    this.accessibility.removeTrapFocus();

    if (clearTerms) {
      this.titleWrapper.innerHTML = "";
      this.popdown.classList.remove("results-visible");
    }
  }

  injectTitle(terms) {
    let title = window.theme.strings.noResultsFor;
    let count = '';
    if (this.result && this.result.products.length > 0) {
      count = this.result.products.length;
      title = window.theme.strings.resultsFor;
    }
    this.popdown.classList.add("results-visible");
    this.titleWrapper.innerHTML = Sqrl.render(this.titleTemplate, {
      count: count,
      title: title,
      query: terms,
    });
  }

  injectProduct(productHTML) {
    this.productWrapper.innerHTML += productHTML;
  }

  fetchProducts(products) {
    const promises = [];
    products.forEach((product) => {
      // because of a translation bug in the predictive search API
      // we need to fetch the product JSON from the handle
      promises.push(
        fetchProduct(product.handle).then((productJSON) => {
          const formatted = formatPrices(productJSON);
          return this.renderProduct(formatted);
        })
      );
    });

    return Promise.all(promises).then((result) => {
      let str = '';
      result.forEach((render) => {
        str += render;
      });
      return str;
    });
  }

  renderProduct(product) {
    let media = null;
    let mediaHover = null;
    let image = '';
    let image_2 = '';

    if (product.media !== undefined) {
      media = product.media[0];
      mediaHover = product.media[1];
    }

    if (media) {
      image = {
        thumb: getSizedImageUrl(media.preview_image.src, '800x800'),
        alt: media.preview_image.src,
      };
    }
    if (mediaHover) {
      image_2 = {
        thumb: getSizedImageUrl(mediaHover.preview_image.src, '800x800'),
        alt: mediaHover.preview_image.src,
      };
    }
    const withImage = {
      ...product,
      image,
      image_2,
    };
    return Sqrl.render(this.productTemplate, {product: withImage});
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

export {SearchPredictive};
